import axios from "axios";
import { API_URL } from "@/config";

const store = {
  namespaced: true,

  actions: {
    async getMany(_, criteria) {
      const response = await axios.get(`${API_URL}/priceLists`, { params: criteria });

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getOne(_, priceListId) {
      const response = await axios.get(`${API_URL}/priceLists/${priceListId}`);

      if (!response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async create(_, priceListData) {
      const response = await axios.post(`${API_URL}/priceLists`, priceListData);

      if (response.status !== 201 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async replaceWithNewOne(_, { priceListId, priceListData }) {
      const response = await axios.post(
        `${API_URL}/priceLists/${priceListId}/replaceWithNewOne`,
        priceListData,
      );

      if (response.status !== 200 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async remove(_, priceListId) {
      const response = await axios.delete(`${API_URL}/priceLists/${priceListId}`);

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async getGroupedByMealTypesPriceListItemsForSubject(
      _,
      { itemsType, subjectType, subjectId, criteria = {} },
    ) {
      const response = await axios.get(
        `${API_URL}/priceLists/${itemsType}/subjects/${subjectType}/${subjectId}/groupedByMealTypes`,
        { params: criteria },
      );

      if (!response.data || typeof response.data.groupedByMealTypesItems !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getGroupedPriceListItemsForSubject(_, { subjectId, subjectType, criteria = {} }) {
      const response = await axios.get(
        `${API_URL}/priceLists/subjects/${subjectType}/${subjectId}/grouped`,
        {
          params: criteria,
        },
      );

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getGroupedPriceListItemsForInstitution(_, { institutionId, criteria = {} }) {
      const response = await axios.get(
        `${API_URL}/priceLists/institutions/${institutionId}/grouped`,
        {
          params: criteria,
        },
      );

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async hasPrepaids() {
      const response = await axios.get(`${API_URL}/priceLists/hasPrepaids`);

      if (!response.data || typeof response.data !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data.hasPrepaids;
    },

    // Groups
    async getManyPriceListsGroups(_, criteria) {
      const response = await axios.get(`${API_URL}/priceLists/groups`, { params: criteria });

      if (!response.data || typeof response.data.items !== "object") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getOnePriceListsGroup(_, priceListsGroupId) {
      const response = await axios.get(`${API_URL}/priceLists/groups/${priceListsGroupId}`);

      if (!response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async createPriceListsGroup(_, priceListsGroupData) {
      const response = await axios.post(`${API_URL}/priceLists/groups`, priceListsGroupData);

      if (response.status !== 201 || !response.data || typeof response.data.id !== "number") {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async updatePriceListsGroup(_, { priceListsGroupId, priceListsGroupData }) {
      const response = await axios.put(
        `${API_URL}/priceLists/groups/${priceListsGroupId}`,
        priceListsGroupData,
      );

      if (response.status !== 200 || !response.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async removePriceListsGroup(_, priceListsGroupId) {
      const response = await axios.delete(`${API_URL}/priceLists/groups/${priceListsGroupId}`);

      if (response.status !== 200 || !response.data.status) {
        throw new Error("Error occurred while processing your request.");
      }

      return true;
    },

    async getAvailableMealTypesAndDiets(_, criteria) {
      const response = await axios.get(`${API_URL}/priceLists/availableMealTypesDiets`, {
        params: criteria,
      });

      if (response.status !== 200 || !response.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },

    async getItemsGroupedByMealTypeCategories(
      _,
      {
        institutionId = null,
        dietId = null,
        priceListsGroupId = null,
        classroomId = null,
        subjectId = null,
        subjectType = null,
        criteria = {},
      },
    ) {
      const response = await axios.get(`${API_URL}/priceLists/itemsGroupedByMealTypeCategories`, {
        params: {
          institutionId,
          dietId,
          priceListsGroupId,
          classroomId,
          subjectId,
          subjectType,
          ...criteria,
        },
      });

      if (response.status !== 200 || !response.data) {
        throw new Error("Error occurred while processing your request.");
      }

      return response.data;
    },
  },
};

export default store;
